import { BackNavigationButton } from "$components/button/back-button";
import { LoadingPage } from "$components/loading/loading-page";
import { lazyLoad } from "$routes/_lib/lazy-load";
import { LocalitiesProvider } from "$routes/_lib/localities-context";
import { gql, useQuery } from "@apollo/client";
import { defineMessages } from "react-intl";
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { linkToAdminControlPanel } from "../admin/links";
import { linkToClinicAdminControlPanel } from "../clinic-admin/links";
import FacilityLocationPageWithBreadcrumbs from "./facilities/[:facilityId]/locations/routes";
import {
  getOrganisationIdQueryParam,
  linkToAddFacilityUser,
  linkToAddNewFacility,
  linkToAddNewOrganisation,
  linkToEditFacility,
  linkToEditFacilityUser,
  linkToEditOrganisation,
  linkToFacilitiesOverview,
  linkToFacilityOverview,
  linkToOrganisationManagement,
  linkToOrganisationOverview,
} from "./links";

const messages = defineMessages({
  backToFacilityOverview: {
    id: "organisationManagement.backToFacilityOverview",
    defaultMessage: "Back to Facility Overview",
  },
  backToOrganizations: {
    id: "organisationManagement.backToOrganizations",
    defaultMessage: "Back to Organizations",
  },
  backToOrganizationOverview: {
    id: "organisationManagement.backToOrganizationOverview",
    defaultMessage: "Back to Organization Overview",
  },
  backToControlPanel: {
    id: "organisationManagement.backToControlPanel",
    defaultMessage: "Back to Control Panel",
  },
  backToFacilities: {
    id: "organisationManagement.backToFacilities",
    defaultMessage: "Back to Facilities",
  },
});

const EditUserPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.EditUserPage })),
);

const FacilityOverviewPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.FacilityOverviewPage })),
);

const FacilitiesListPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.FacilitiesListPage })),
);

const EditFacilityPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.EditFacilityPage })),
);

const CreateNewUserPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.CreateNewUserPage })),
);

const OrganisationOverviewPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.OrganisationOverviewPage })),
);

const EditOrganisationPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.EditOrganisationPage })),
);

const CreateNewOrganisation = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.CreateNewOrganisationPage })),
);

const OrganisationListPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.OrganisationsListPage })),
);

const CreateNewFacilityPage = lazyLoad(() =>
  import(
    /* webpackChunkName: "OrganisationManagementChunk" */
    "./chunk"
  ).then((module) => ({ default: module.default.CreateNewFacilityPage })),
);

const EditFacilityUserWithBreadcrumbs = () => {
  const { userId, facilityId } = useParams();
  const organisationId = getOrganisationIdQueryParam();

  return (
    <>
      <BackNavigationButton
        to={linkToFacilityOverview({ facilityId, organisationId })}
        label={messages.backToFacilityOverview}
      />
      <EditUserPage _id={userId} organisationId={organisationId} />
    </>
  );
};

const FacilityRouter: FC<{ userRole: string }> = ({ userRole }) => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const organisationId = getOrganisationIdQueryParam();

  return (
    <Routes>
      <Route
        path="overview"
        element={
          <FacilityOverviewPage
            facilityId={facilityId}
            pathToAddUser={linkToAddFacilityUser({
              facilityId,
              organisationId,
            })}
            pathToEditFacility={linkToEditFacility({
              facilityId,
              organisationId,
            })}
            navigateToEditUser={(userId: string) =>
              navigate(
                linkToEditFacilityUser({
                  facilityId,
                  userId,
                  organisationId,
                }),
              )
            }
            controlPanelLink={
              userRole === "clinic"
                ? linkToClinicAdminControlPanel()
                : linkToAdminControlPanel()
            }
          />
        }
      />

      <Route
        path="edit"
        element={
          <>
            <BackNavigationButton
              to={linkToFacilityOverview({ facilityId, organisationId })}
              label={messages.backToFacilityOverview}
            />
            <EditFacilityPage
              facilityId={facilityId}
              organisationId={organisationId}
            />
          </>
        }
      />

      <Route
        path="user/:userId/edit"
        element={<EditFacilityUserWithBreadcrumbs />}
      />

      <Route
        path="locations"
        element={
          <FacilityLocationPageWithBreadcrumbs
            facilityId={facilityId}
            userRole={userRole}
          />
        }
      />
      <Route
        path="add-user"
        element={
          <>
            <BackNavigationButton
              to={linkToFacilityOverview({ facilityId, organisationId })}
              label={messages.backToFacilityOverview}
            />
            <CreateNewUserPage
              facilityId={facilityId}
              organisationId={organisationId}
            />
          </>
        }
      />
    </Routes>
  );
};

const OrganisationRouter = () => {
  const { organisationId } = useParams();
  const navigate = useNavigate();

  return (
    <Routes>
      <Route
        path="overview"
        element={
          <>
            <BackNavigationButton
              to={linkToOrganisationManagement()}
              label={messages.backToOrganizations}
            />
            <OrganisationOverviewPage
              organisationId={organisationId}
              pathToEditOrganisation={linkToEditOrganisation({
                organisationId,
              })}
              pathToAddFacility={linkToAddNewFacility({ organisationId })}
              navigateToFacilityOverview={({ facilityId }) =>
                navigate(linkToFacilityOverview({ facilityId, organisationId }))
              }
            />
          </>
        }
      />
      <Route
        path="edit"
        element={
          <>
            <BackNavigationButton
              to={linkToOrganisationOverview({ organisationId })}
              label={messages.backToOrganizationOverview}
            />
            <EditOrganisationPage organisationId={organisationId} />
          </>
        }
      />
    </Routes>
  );
};

const FacilitiesRouter: FC<{
  userRole: string;
  userFacilityId: string;
}> = ({ userRole, userFacilityId }) => {
  const organisationId = getOrganisationIdQueryParam();
  const navigate = useNavigate();

  return (
    <Routes>
      {/* Leaf nodes */}
      <Route
        path="overview"
        element={
          <>
            <BackNavigationButton
              to={
                userRole === "clinic"
                  ? linkToClinicAdminControlPanel()
                  : linkToAdminControlPanel()
              }
              label={messages.backToControlPanel}
            />
            <FacilitiesListPage
              pathToAddFacility={linkToAddNewFacility()}
              navigateToFacilityOverview={({ facilityId }) =>
                navigate(linkToFacilityOverview({ facilityId }))
              }
            />
          </>
        }
      />
      <Route
        path="create"
        element={
          <>
            <BackNavigationButton
              to={linkToFacilitiesOverview()}
              label={messages.backToFacilities}
            />
            <CreateNewFacilityPage />
          </>
        }
      />
      <Route
        path="my-facility"
        element={
          <Navigate
            replace
            to={linkToFacilityOverview({
              facilityId: userFacilityId,
              organisationId: undefined,
            })}
          />
        }
      />
      <Route
        path="facility/:facilityId/*"
        element={<FacilityRouter userRole={userRole} />}
      />
    </Routes>
  );
};

// TODO: add default redirects to all routers
function OrganisationManagementRouter() {
  const { data, loading } = useQuery(gql`
    query getMyRoleName {
      myUser {
        clinic
        organisation
      }
      myLegacyRole {
        name
      }
    }
  `);
  const userRole = data?.myLegacyRole?.name;
  const userFacilityId = data?.myUser?.clinic;
  const navigate = useNavigate();
  const navigateToOrganisationOverview = ({ organisationId }) =>
    navigate(linkToOrganisationOverview({ organisationId }));

  if (loading) {
    return <LoadingPage />;
  }

  return (
    <LocalitiesProvider>
      <Routes>
        <Route>
          <Route
            path="overview"
            element={
              <>
                <BackNavigationButton
                  to={linkToAdminControlPanel()}
                  label={messages.backToControlPanel}
                />
                <OrganisationListPage
                  pathToAddOrganisation={linkToAddNewOrganisation()}
                  navigateToOrganisationOverview={
                    navigateToOrganisationOverview
                  }
                />
              </>
            }
          />
          <Route
            path="create"
            element={
              <>
                <BackNavigationButton
                  to={linkToOrganisationManagement()}
                  label={messages.backToOrganizations}
                />
                <CreateNewOrganisation />
              </>
            }
          />
          <Route
            path="organisation/:organisationId/*"
            element={<OrganisationRouter />}
          />
          <Route
            path="facilities/*"
            element={
              <FacilitiesRouter
                userRole={userRole}
                userFacilityId={userFacilityId}
              />
            }
          />
        </Route>
      </Routes>
    </LocalitiesProvider>
  );
}

export function OrganisationManagementRoutes() {
  return <OrganisationManagementRouter />;
}
